import { Form, Space, Switch, Typography } from 'antd';
import { CloseOutlined, PlusOutlined } from '@ant-design/icons';
import { useAuthStore } from '@stores/auth.store';
import Table from '@/components/ui/table/Table';
import {
    DashboardContainer,
    StudentsHeaderActions,
    StudentsHeaderLeft,
    StudentsHeaderRow,
    StudentsHeaderSub,
    StudentsHeaderTitle,
} from './style';
import { AppModal } from '@/components/ui/AppModal';
import { AppInput, AppTextArea } from '@/components/ui/AppInput';
import { AppSelect } from '@/components/ui/AppSelect';
import { FormGrid, FormGridItemFull, SectionHeader } from '@/components/ui/FormGrid';
import { SelectionChip, SelectionChipRemoveButton, SelectionChipWrap, SelectionSummary } from '@/components/ui/SelectionChips';
import { Button } from '@components/ui/Button';
import { useHomeworkDiaryManager } from '@hooks/tenant/useHomeworkDiaryManager.jsx';
import { AppModalShellGlobalStyles } from '@/components/ui/AppModalShellStyles';

const { Text } = Typography;

export default function Homeworks() {
    const user = useAuthStore((s) => s.user);
    const tenantId = useAuthStore((s) => s.user?.tenantId);
    const {
        form,
        mode,
        modalOpen,
        taskModalOpen,
        editingTaskIndex,
        taskDraft,
        taskDraftHasValues,
        gradePickerValue,
        selectedAudienceType,
        isTeacherUser,
        isStaffUser,
        selectableClassOptions,
        selectedClassChips,
        teacherOptions,
        homeworkColumns,
        taskColumns,
        filteredRows,
        loading,
        total,
        searchText,
        currentPage,
        currentPageSize,
        taskRows,
        createHomeworkMutation,
        updateHomeworkMutation,
        deleteHomeworkMutation,
        setCurrentPage,
        setCurrentPageSize,
        setSearchText,
        setTaskDraft,
        openCreate,
        openEdit,
        confirmDelete,
        resetMainModal,
        handleSubmit,
        openTaskCreateModal,
        openTaskEditModal,
        removeTask,
        closeTaskModal,
        saveTaskDraft,
        resetTaskDraft,
        addSelectedClass,
        removeSelectedClass,
    } = useHomeworkDiaryManager({ tenantId, user });

    return (
        <DashboardContainer>
            <AppModalShellGlobalStyles />
            <StudentsHeaderRow>
                <StudentsHeaderLeft>
                    <StudentsHeaderTitle>Homework Diary</StudentsHeaderTitle>
                    <StudentsHeaderSub>Create daily homework with professional todo lists for all or grade-wise students.</StudentsHeaderSub>
                </StudentsHeaderLeft>
                <StudentsHeaderActions>
                    <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
                        Add Homework
                    </Button>
                </StudentsHeaderActions>
            </StudentsHeaderRow>

            <Table
                title="Daily Homework List"
                columns={homeworkColumns}
                dataSource={filteredRows}
                loading={loading}
                searchValue={searchText}
                onSearchChange={setSearchText}
                showFilter={false}
                pagination
                pageSize={currentPageSize}
                serverPagination
                currentPage={currentPage}
                totalItems={searchText ? filteredRows.length : total}
                onPageChange={(page, size) => {
                    setCurrentPage(page);
                    if (size && size !== currentPageSize) {
                        setCurrentPageSize(size);
                        setCurrentPage(1);
                    }
                }}
                onRowClick={openEdit}
            />

            <AppModal
                title={mode === 'create' ? 'Create Homework Diary' : 'Edit Homework Diary'}
                open={modalOpen}
                onCancel={resetMainModal}
                onOk={handleSubmit}
                confirmLoading={createHomeworkMutation.isPending || updateHomeworkMutation.isPending}
                okText={mode === 'create' ? 'Save homework' : 'Save changes'}
                size="lg"
                width="min(86vw, 980px)"
                className="app-modal-shell--tall-scroll"
            >
                <Form
                    form={form}
                    layout="vertical"
                    initialValues={{ audienceType: 'all', status: true, todos: [] }}
                >
                    <Form.Item name="title" label="Homework title" rules={[{ required: true, message: 'Homework title is required' }]}>
                        <AppInput placeholder="e.g. Daily Math Practice" />
                    </Form.Item>
                    <Form.Item name="description" label="Description">
                        <AppTextArea rows={3} placeholder="Optional notes for students and parents" />
                    </Form.Item>
                    <FormGrid>
                        <Form.Item name="dueDate" label="Due date" rules={[{ required: true, message: 'Due date is required' }]}>
                            <AppInput type="date" />
                        </Form.Item>
                        <Form.Item name="audienceType" label="Assign to">
                            <AppSelect
                                options={[
                                    { value: 'all', label: 'All students' },
                                    { value: 'grade', label: 'Grade wise' },
                                ]}
                            />
                        </Form.Item>
                        {selectedAudienceType === 'grade' ? (
                            <FormGridItemFull>
                                <Form.Item name="classIds" hidden>
                                    <AppSelect mode="multiple" options={[]} />
                                </Form.Item>
                                <Form.Item label="Select grades/classes" required>
                                    <AppSelect
                                        value={gradePickerValue}
                                        onChange={addSelectedClass}
                                        options={selectableClassOptions}
                                        placeholder="Select one grade/class"
                                        showSearch
                                        optionFilterProp="label"
                                        allowClear
                                    />
                                </Form.Item>
                            </FormGridItemFull>
                        ) : null}
                        {selectedAudienceType === 'grade' ? (
                            <SelectionSummary>
                                <Text type="secondary">
                                    Selected Grades
                                </Text>
                                {selectedClassChips.length ? (
                                    <SelectionChipWrap>
                                        {selectedClassChips.map((chip) => (
                                            <SelectionChip key={chip.id}>
                                                <span>{chip.label}</span>
                                                <SelectionChipRemoveButton
                                                    type="button"
                                                    onClick={() => removeSelectedClass(chip.id)}
                                                    aria-label={`Remove ${chip.label}`}
                                                >
                                                    <CloseOutlined />
                                                </SelectionChipRemoveButton>
                                            </SelectionChip>
                                        ))}
                                    </SelectionChipWrap>
                                ) : (
                                    <Text type="secondary">No grades selected yet.</Text>
                                )}
                            </SelectionSummary>
                        ) : null}
                        {!isTeacherUser ? (
                            <FormGridItemFull>
                                <Form.Item
                                    name="assignedByTeacherId"
                                    label="Assigned By (Teacher)"
                                    rules={isStaffUser && mode === 'create' ? [{ required: true, message: 'Please select a teacher' }] : []}
                                >
                                    <AppSelect
                                        options={teacherOptions}
                                        placeholder="Select teacher"
                                        showSearch
                                        optionFilterProp="label"
                                    />
                                </Form.Item>
                            </FormGridItemFull>
                        ) : null}
                    </FormGrid>
                    {mode === 'edit' ? (
                        <Form.Item label="Active">
                            <Form.Item name="status" noStyle valuePropName="checked">
                                <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
                            </Form.Item>
                        </Form.Item>
                    ) : null}

                    <SectionHeader>
                        <Text strong>Task list</Text>
                        <Button type="button" icon={<PlusOutlined />} $primary={false} onClick={openTaskCreateModal}>
                            Add Task
                        </Button>
                    </SectionHeader>
                    <Form.Item
                        name="todos"
                        rules={[
                            {
                                validator: (_, value) => {
                                    if (Array.isArray(value) && value.length > 0) return Promise.resolve();
                                    return Promise.reject(new Error('Please add at least one task'));
                                },
                            },
                        ]}
                    >
                        <Table
                            title=""
                            columns={taskColumns}
                            dataSource={taskRows}
                            showSearch={false}
                            showFilter={false}
                            showMore={false}
                            pagination={false}
                            emptyText="No task added yet."
                            $topMargin={false}
                        />
                    </Form.Item>
                </Form>
            </AppModal>

            <AppModal
                title={editingTaskIndex === null ? 'Add Task' : 'Edit Task'}
                open={taskModalOpen}
                onCancel={closeTaskModal}
                onOk={saveTaskDraft}
                okText={editingTaskIndex === null ? 'Save Task' : 'Update Task'}
                footer={(
                    <Space>
                        {taskDraftHasValues ? (
                            <Button type="button" $primary={false} onClick={resetTaskDraft}>
                                Clear
                            </Button>
                        ) : null}
                        <Button type="button" $primary={false} onClick={closeTaskModal}>
                            Cancel
                        </Button>
                        <Button type="button" onClick={saveTaskDraft}>
                            {editingTaskIndex === null ? 'Save Task' : 'Update Task'}
                        </Button>
                    </Space>
                )}
                size="md"
                destroyOnClose
            >
                <Form layout="vertical">
                    <Form.Item label="Task" required>
                        <AppInput
                            value={taskDraft.title}
                            onChange={(event) => setTaskDraft((prev) => ({ ...prev, title: event.target.value }))}
                            placeholder="e.g. Solve exercises 1-10"
                        />
                    </Form.Item>
                    <Form.Item label="Details">
                        <AppTextArea
                            rows={3}
                            value={taskDraft.note}
                            onChange={(event) => setTaskDraft((prev) => ({ ...prev, note: event.target.value }))}
                            placeholder="Optional task details"
                        />
                    </Form.Item>
                    <Form.Item label="Required">
                        <Switch
                            checked={taskDraft.required}
                            checkedChildren="Yes"
                            unCheckedChildren="No"
                            onChange={(checked) => setTaskDraft((prev) => ({ ...prev, required: checked }))}
                        />
                    </Form.Item>
                </Form>
            </AppModal>
        </DashboardContainer>
    );
}
